<template>
  <div
    v-if="isVisible"
    class="eg-modal"
    :class="[{ 'eg-modal--force-backdrop': showBackdrop }, modalClass]"
    :style="{ zIndex: zIndex }"
    @click="handleOverlayClick"
  >
    <div class="eg-modal__overlay"></div>
    <div
      class="eg-modal__container"
      :class="containerClasses"
      role="dialog"
      :aria-labelledby="titleId"
      :aria-describedby="bodyId"
      aria-modal="true"
      @click.stop
    >
      <div class="eg-modal__content">
        <!-- Header -->
        <div v-if="!hideTitle && ($slots.header || title)">
          <slot name="header" :close="handleClose">
            <eg-modal-header
              :title="title"
              :closable="closable"
              @onClose="handleClose"
            />
          </slot>
        </div>

        <!-- Body -->
        <div :id="bodyId" class="eg-modal__body">
          <div
            v-if="loading && $slots.loading"
            class="eg-modal__loading d-flex flex-column align-items-center justify-content-center"
          >
            <slot name="loading"></slot>
          </div>
          <div
            v-else-if="loading"
            class="eg-modal__loading d-flex flex-column align-items-center justify-content-center"
          >
            <div class="eg-modal__spinner"></div>
            <p class="eg-modal__loading-text">
              {{ $t("common.loadingEllipsis") }}
            </p>
          </div>
          <template v-else>
            <slot></slot>
          </template>
        </div>

        <!-- Footer -->
        <div
          v-if="
            !hideFooter &&
            ($scopedSlots['modal-footer'] || $scopedSlots['footer'])
          "
        >
          <eg-modal-footer>
            <slot name="modal-footer" :close="handleClose"></slot>
            <slot name="footer" :close="handleClose"></slot>
          </eg-modal-footer>
        </div>
        <slot name="rubric-modal-footer" :close="handleClose"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import EgModalHeader from "./eg_modal_header.vue";
import EgModalFooter from "./eg_modal_footer.vue";

export default {
  name: "EgModal",
  components: {
    EgModalHeader,
    EgModalFooter,
  },

  props: {
    // Vue 2/3 compatible v-model
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Legacy Vue 2 support
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "medium",
      validator: (value) =>
        [
          "small",
          "medium",
          "large",
          "fullscreen",
          "content",
          "sm",
          "md",
          "lg",
        ].includes(value),
    },
    closable: {
      type: Boolean,
      default: true,
    },
    isLostDataModal: {
      type: Boolean,
      default: false,
    },
    lostDataWarning: {
      type: Object,
      default: () => {},
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    zIndex: {
      type: [String, Number],
      default: 1000,
    },
    closeAriaLabel: {
      type: String,
      default: "Close modal",
    },

    hideTitle: {
      type: Boolean,
      default: false,
    },

    hideFooter: {
      type: Boolean,
      default: false,
    },

    // The id of the modal, need if want to register on the modal manager
    id: {
      type: String,
      default: "",
    },

    // Force show backdrop even if not the first modal
    showBackdrop: {
      type: Boolean,
      default: false,
    },

    // Custom CSS class to add to the modal wrapper
    modalClass: {
      type: [String, Array, Object],
      default: "",
    },
  },
  emits: ["update:modelValue", "update:value", "close", "open"],
  data() {
    return {
      isVisible: false,
      titleId: `modal-title-${
        this._uid || Math.random().toString(36).substr(2, 9)
      }`,
      bodyId: `modal-body-${
        this._uid || Math.random().toString(36).substr(2, 9)
      }`,
    };
  },
  computed: {
    containerClasses() {
      return [
        `eg-modal__container--${this.size}`,
        {
          "eg-modal__container--loading": this.loading,
        },
      ];
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        this.handleVisibilityChange(newValue);
      },
    },
    value: {
      immediate: true,
      handler(newValue) {
        this.handleVisibilityChange(newValue);
      },
    },
  },

  created() {
    if (this.id) {
      this.$egModal.registerModal(this.id, this);
    }
  },

  mounted() {
    document.addEventListener("keydown", this.handleEscapeKey);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEscapeKey);
    this.enableBodyScroll();

    if (this.id) {
      this.$egModal.unregisterModal(this.id);
    }
  },

  methods: {
    handleVisibilityChange(visible) {
      if (visible && !this.isVisible) {
        this.show();
      } else if (!visible && this.isVisible) {
        this.hide();
      }
    },
    show() {
      this.isVisible = true;
      this.disableBodyScroll();
      this.$emit("open");
    },
    // hide without emitting events, need for modal manager
    silentHide() {
      this.isVisible = false;
      this.enableBodyScroll();
    },
    hide() {
      this.silentHide();
      this.$emit("close");
      this.$emit("update:modelValue", false);
      this.$emit("update:value", false);
      this.$emit("input", false);
    },
    async handleClose(isConfirmed = true) {
      if (this.closable && isConfirmed) {
        const leave = this.isLostDataModal
          ? await this.$showConfirmationLeaveModal({
              text: this.lostDataWarning.text,
              okTitle: this.lostDataWarning.okTitle,
            })
          : true;

        if (leave) {
          this.hide();
          return true;
        } else {
          return false;
        }
      }
    },
    async handleOverlayClick() {
      if (!this.persistent) {
        this.handleClose();
      }
    },
    handleEscapeKey(event) {
      if (event.key === "Escape" && this.isVisible && this.closable) {
        this.handleClose();
      }
    },
    disableBodyScroll() {
      document.body.style.overflow = "hidden";
    },
    enableBodyScroll() {
      document.body.style.overflow = "";
    },
  },
};
</script>

<style lang="scss" scoped>
.eg-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  z-index: 1;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  ::v-deep {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        sans-serif;
    }
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(2px);
    z-index: 0;
  }

  // Hide overlay for all modals except the first one

  &:not(:first-child):not(.eg-modal--force-backdrop) {
    .eg-modal__overlay {
      display: none;
    }
  }

  &__container {
    position: relative;
    width: 100%;
    min-width: 400px;
    max-height: 85vh;
    max-height: 85dvh;
    z-index: 1;
    animation: modalEnter 0.2s ease-out;

    // Size variants
    &--sm,
    &--small {
      max-width: 400px;
    }

    &--md,
    &--medium {
      max-width: min(508px, 90vw);
    }

    &--lg,
    &--large {
      max-width: min(932px, 90vw);
    }

    .eg-modal__content {
      border-radius: 16px;
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .eg-modal__body {
      flex: 1;
      overflow-y: auto;
    }

    &--fullscreen {
      max-width: none;
      width: 98vw;
      height: 98vh;
      height: 98dvh;
      max-height: 98vh;
      max-height: 98dvh;
      margin: 0;
      padding: 0;
    }

    &--content {
      max-width: 98vw;
      max-height: 98vh;
      max-height: 98dvh;
      width: auto;
      height: auto;
      margin: 0;
      padding: 0;
    }

    // Mobile responsive
    @media (max-width: 1040px) {
      &:not(&--fullscreen) {
        min-width: 90vw;
        width: 90vw;
        max-width: none;
        margin: 0 5vw;
      }
    }
  }

  &__content {
    background-color: var(--color-surface-neutral-default, #ffffff);
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
    border: 1px solid var(--color-stroke-neutral-default, #e6e6e6);
    overflow: hidden;
    max-height: inherit;
  }

  &__body {
    padding: 24px;
    color: var(--color-text-neutral-on-white, #1a1a1a);
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
  }

  &__spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-stroke-neutral-container, #e6e6e6);
    border-top: 3px solid var(--color-stroke-primary, #8a3ffc);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  &__loading-text {
    margin: 0;
    font-size: 14px;
    color: var(--color-text-secondary, #666666);
  }

  // Remove header margin when no title
  &__header:empty + &__body {
    padding-top: 24px;
  }

  // Remove footer margin when no footer
  &__body:last-child {
    padding-bottom: 24px;
  }
}

@keyframes modalEnter {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Global styles for modal backdrop
:global(.eg-modal-open) {
  overflow: hidden;
}
</style>
